import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import CardIcon from "@material-ui/icons/CreditCardOutlined";
import PixIcon from "@material-ui/icons/CropFree";
import clsx from "clsx";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useMenuStyles } from "../../components/menuStyles";
import { toast } from "react-toastify";

const useStyles = makeStyles(theme => ({
  lead: { fontSize: 13, color: theme.app.textSecondary, lineHeight: 1.5, marginBottom: 16 },
  methods: { display: "flex", gap: 10, marginBottom: 18 },
  method: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    borderRadius: 12,
    border: `1px solid ${theme.app.border}`,
    cursor: "pointer",
    "&:hover": { background: theme.app.hover },
    "& svg": { fontSize: 22, color: theme.app.textTertiary },
  },
  methodOn: {
    borderColor: theme.app.dark,
    boxShadow: `0 0 0 1px ${theme.app.dark}`,
    "& svg": { color: theme.app.ink },
  },
  methodName: { fontSize: 13.5, fontWeight: 600, color: theme.app.ink },
  methodDesc: { fontSize: 11.5, color: theme.app.textSecondary },
  row: { display: "flex", gap: 10 },
  price: { fontSize: 13, color: theme.app.ink, fontWeight: 600, marginBottom: 14 },
  saved: {
    display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
    border: `1px solid ${theme.app.border}`, borderRadius: 10, marginBottom: 8, cursor: "pointer",
    "&:hover": { background: theme.app.hover },
  },
  savedOn: { borderColor: theme.app.dark, boxShadow: `0 0 0 1px ${theme.app.dark}` },
  pixBox: { textAlign: "center", padding: "8px 0" },
  pixCode: {
    fontFamily: theme.app.mono, fontSize: 11, wordBreak: "break-all", background: theme.app.chatBg,
    border: `1px solid ${theme.app.border}`, borderRadius: 8, padding: 10, margin: "12px 0", textAlign: "left",
  },
  warn: { fontSize: 12.5, color: "#9A6410", background: "#FBF1DE", border: "1px solid #EBD9B4", borderRadius: 8, padding: "9px 11px", marginBottom: 14 },
}));

const brl = c => `R$ ${((c || 0) / 100).toFixed(2).replace(".", ",")}`;
const digits = s => String(s || "").replace(/\D/g, "");

// Tokeniza o cartão NO NAVEGADOR, com a chave pública. É o que mantém o número
// do cartão fora do nosso backend e dos nossos logs — o servidor só vê o token.
const tokenizeCard = async (publicKey, card) => {
  const r = await fetch(`https://api.pagar.me/core/v5/tokens?appId=${encodeURIComponent(publicKey)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      type: "card",
      card: {
        number: digits(card.number),
        holder_name: card.holderName,
        exp_month: Number(card.expMonth),
        exp_year: Number(card.expYear),
        cvv: digits(card.cvv),
      },
    }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok || !data.id) {
    throw new Error((data.errors && JSON.stringify(data.errors)) || data.message || "Não foi possível validar o cartão.");
  }
  return data.id;
};

const CheckoutModal = ({ plan, open, onClose, onDone }) => {
  const classes = useStyles();
  const menu = useMenuStyles();
  const [method, setMethod] = useState("card");
  const [cards, setCards] = useState([]);
  const [publicKey, setPublicKey] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [form, setForm] = useState({ number: "", holderName: "", expMonth: "", expYear: "", cvv: "", document: "", phone: "", zip: "", line1: "", city: "", state: "" });
  const [busy, setBusy] = useState(false);
  const [pix, setPix] = useState(null); // fatura de PIX aguardando pagamento

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const loadCards = useCallback(async () => {
    try {
      const { data } = await api.get("/me/payment-methods");
      setCards(data.cards || []);
      setPublicKey(data.publicKey || null);
      const def = (data.cards || []).find(c => c.isDefault) || (data.cards || [])[0];
      if (def) setSelectedCard(def.id);
    } catch (err) {
      /* sem cartões ainda */
    }
  }, []);

  useEffect(() => { if (open) loadCards(); }, [open, loadCards]);

  // PIX: a confirmação chega por webhook, então a tela PERGUNTA pela fatura até
  // ela virar paga. Sem isso o cliente pagaria e continuaria olhando o QR.
  useEffect(() => {
    if (!pix || !open) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/me/invoices");
        const inv = (data.invoices || []).find(i => i.id === pix.id);
        if (inv && inv.status === "paid") {
          clearInterval(t);
          toast.success("Pagamento confirmado!");
          onDone && onDone();
        }
        if (inv && inv.status === "expired") {
          clearInterval(t);
          toast.error("O código PIX expirou. Gere um novo.");
          setPix(null);
        }
      } catch (e) {
        /* segue tentando */
      }
    }, 5000);
    return () => clearInterval(t);
  }, [pix, open, onDone]);

  const subscribe = async paymentMethodId => {
    const { data } = await api.post("/me/subscribe", {
      plan: plan.code,
      method,
      paymentMethodId: paymentMethodId || undefined,
      document: form.document || undefined,
      phone: form.phone || undefined,
    });
    return data;
  };

  const confirmCard = async () => {
    setBusy(true);
    try {
      let cardId = selectedCard;
      if (!cardId) {
        if (!publicKey) throw new Error("Pagamento por cartão indisponível no momento.");
        const token = await tokenizeCard(publicKey, form);
        const { data } = await api.post("/me/payment-methods", {
          token,
          holderName: form.holderName,
          document: form.document,
          phone: form.phone,
          billingAddress: { line_1: form.line1, zip_code: digits(form.zip), city: form.city, state: form.state, country: "BR" },
        });
        cardId = data.id;
      }
      const r = await subscribe(cardId);
      if (r.invoice && r.invoice.status !== "paid") {
        throw new Error(r.invoice.lastError || "A cobrança não foi aprovada.");
      }
      toast.success(`Plano ${plan.name} contratado.`);
      onDone && onDone();
    } catch (err) {
      toastError(err.response ? err : { message: err.message });
    } finally {
      setBusy(false);
    }
  };

  const confirmPix = async () => {
    setBusy(true);
    try {
      const r = await subscribe(null);
      if (!r.invoice || !r.invoice.pixQrCode) {
        throw new Error(r.invoice?.lastError || "Não foi possível gerar o PIX.");
      }
      setPix(r.invoice);
    } catch (err) {
      toastError(err.response ? err : { message: err.message });
    } finally {
      setBusy(false);
    }
  };

  if (!plan) return null;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ className: menu.dialogPaper }}>
      <DialogTitle>Contratar {plan.name}</DialogTitle>
      <DialogContent dividers>
        <div className={classes.price}>{brl(plan.monthlyPriceCents)} por mês</div>

        {pix ? (
          <div className={classes.pixBox}>
            <Typography className={classes.lead}>
              Abra o app do seu banco, escolha PIX e use o código abaixo. A confirmação é automática —
              pode deixar esta tela aberta.
            </Typography>
            <div className={classes.pixCode}>{pix.pixQrCode}</div>
            <Button
              variant="outlined"
              onClick={() => { navigator.clipboard.writeText(pix.pixQrCode); toast.success("Código copiado"); }}
            >
              Copiar código PIX
            </Button>
            <Typography className={classes.lead} style={{ marginTop: 14 }}>
              <CircularProgress size={13} style={{ marginRight: 8, verticalAlign: "middle" }} />
              Aguardando o pagamento…
            </Typography>
          </div>
        ) : (
          <>
            <div className={classes.methods}>
              <div className={clsx(classes.method, method === "card" && classes.methodOn)} onClick={() => setMethod("card")}>
                <CardIcon />
                <span>
                  <div className={classes.methodName}>Cartão</div>
                  <div className={classes.methodDesc}>Renova sozinho todo mês</div>
                </span>
              </div>
              <div className={clsx(classes.method, method === "pix" && classes.methodOn)} onClick={() => setMethod("pix")}>
                <PixIcon />
                <span>
                  <div className={classes.methodName}>PIX</div>
                  <div className={classes.methodDesc}>Você paga a cada ciclo</div>
                </span>
              </div>
            </div>

            {method === "pix" && (
              <>
                <Typography className={classes.lead}>
                  O PIX não renova sozinho: a cada mês você recebe uma nova cobrança para pagar. Se
                  preferir não se preocupar com isso, use cartão.
                </Typography>
                {/* o gateway exige documento em toda cobrança, e no PIX esta é a
                    única chance de coletar — não há cadastro de cartão junto */}
                <div className={classes.row}>
                  <TextField label="CPF/CNPJ do titular" fullWidth margin="dense" size="small" variant="outlined"
                    value={form.document} onChange={e => set("document", e.target.value)} />
                  <TextField label="Telefone com DDD" fullWidth margin="dense" size="small" variant="outlined"
                    value={form.phone} onChange={e => set("phone", e.target.value)} />
                </div>
              </>
            )}

            {method === "card" && (
              <>
                {cards.map(c => (
                  <div
                    key={c.id}
                    className={clsx(classes.saved, selectedCard === c.id && classes.savedOn)}
                    onClick={() => setSelectedCard(c.id)}
                  >
                    <CardIcon />
                    <span className={classes.methodName}>{c.brand} •••• {c.last4}</span>
                  </div>
                ))}
                {cards.length > 0 && (
                  <div
                    className={clsx(classes.saved, !selectedCard && classes.savedOn)}
                    onClick={() => setSelectedCard(null)}
                  >
                    <span className={classes.methodName}>Usar outro cartão</span>
                  </div>
                )}

                {!selectedCard && (
                  <>
                    {!publicKey && (
                      <div className={classes.warn}>
                        Pagamento por cartão ainda não está configurado nesta instalação.
                      </div>
                    )}
                    <TextField label="Número do cartão" fullWidth margin="dense" size="small" variant="outlined"
                      value={form.number} onChange={e => set("number", e.target.value)} />
                    <TextField label="Nome impresso no cartão" fullWidth margin="dense" size="small" variant="outlined"
                      value={form.holderName} onChange={e => set("holderName", e.target.value)} />
                    <div className={classes.row}>
                      <TextField label="Mês" margin="dense" size="small" variant="outlined" style={{ width: 90 }}
                        value={form.expMonth} onChange={e => set("expMonth", e.target.value)} />
                      <TextField label="Ano" margin="dense" size="small" variant="outlined" style={{ width: 100 }}
                        value={form.expYear} onChange={e => set("expYear", e.target.value)} />
                      <TextField label="CVV" margin="dense" size="small" variant="outlined" style={{ width: 90 }}
                        value={form.cvv} onChange={e => set("cvv", e.target.value)} />
                      <TextField label="CPF/CNPJ do titular" margin="dense" size="small" variant="outlined" style={{ flex: 1 }}
                        value={form.document} onChange={e => set("document", e.target.value)} />
                    </div>
                    <div className={classes.row}>
                      {/* o gateway exige telefone do titular em toda cobrança */}
                      <TextField label="Telefone com DDD" margin="dense" size="small" variant="outlined" style={{ flex: 1 }}
                        value={form.phone} onChange={e => set("phone", e.target.value)} />
                    </div>
                    <div className={classes.row}>
                      <TextField label="Endereço" margin="dense" size="small" variant="outlined" style={{ flex: 1 }}
                        value={form.line1} onChange={e => set("line1", e.target.value)} />
                      <TextField label="CEP" margin="dense" size="small" variant="outlined" style={{ width: 120 }}
                        value={form.zip} onChange={e => set("zip", e.target.value)} />
                    </div>
                    <div className={classes.row}>
                      <TextField label="Cidade" margin="dense" size="small" variant="outlined" style={{ flex: 1 }}
                        value={form.city} onChange={e => set("city", e.target.value)} />
                      <TextField label="UF" margin="dense" size="small" variant="outlined" style={{ width: 90 }}
                        value={form.state} onChange={e => set("state", e.target.value)} />
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{pix ? "Fechar" : "Cancelar"}</Button>
        {!pix && (
          <Button
            variant="contained"
            color="secondary"
            disabled={busy}
            onClick={method === "pix" ? confirmPix : confirmCard}
          >
            {busy ? "Processando…" : method === "pix" ? "Gerar PIX" : `Pagar ${brl(plan.monthlyPriceCents)}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CheckoutModal;
